import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import React, { useState } from 'react'

import { StarFilledIcon } from '../../icons'
import Typography from './typography'

interface RatingProps {
  max?: number
  onChange?: (value: number) => void
  defaultValue?: number
  precision?: 0.5 | 1 // you can extend to 0.1 if needed,
  mode?: 'view' | 'edit'
  // How the value is printed next to the stars (e.g. a localized average), raw by default
  formatValue?: (value: number) => string
}

const Rating: React.FC<RatingProps> = ({
  max = 5,
  onChange,
  defaultValue = 0,
  precision = 0.5,
  mode = 'edit',
  formatValue
}) => {
  const [rating, setRating] = useState<number>(defaultValue)
  const [hovered, setHovered] = useState<number | null>(null)

  // The pointed star (or half star) is selected: round up, never below the first step of that star
  const roundToPrecision = (index: number, percent: number) =>
    Math.max(index + precision, Math.ceil((index + percent) / precision) * precision)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    const { left, width } = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - left
    const percent = x / width
    const value = roundToPrecision(index, percent)

    if (value === rating) {
      setRating(0)
      onChange?.(0)
    } else {
      setRating(value)
      onChange?.(value)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    const { left, width } = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - left
    const percent = x / width
    const value = roundToPrecision(index, percent)
    setHovered(value)
  }

  const getFillState = (index: number) => {
    const currentValue = hovered ?? rating
    if (currentValue >= index + 1) return 'full'
    if (currentValue >= index + 0.5) return 'half'
    return 'empty'
  }

  return (
    <div className="flex gap-2 items-center">
      <div className="flex gap-1">
        {Array.from({ length: max }, (_, i) => {
          const fill = getFillState(i)
          const fillColor = tailwindColors.yellow.DEFAULT
          const emptyColor = tailwindColors.grey.stroke

          return (
            <button
              key={i}
              type="button"
              className={cn('p-0 border-0 bg-transparent cursor-pointer', {
                'pointer-events-none': mode === 'view'
              })}
              onClick={(e) => handleClick(e, i)}
              onMouseMove={(e) => handleMouseMove(e, i)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="relative w-6 h-6">
                <div className="absolute top-0 left-0 w-full h-full">
                  <StarFilledIcon color={emptyColor} color2="transparent" />
                </div>
                {fill !== 'empty' && (
                  <div
                    className="absolute top-0 left-0 h-full overflow-hidden"
                    style={{
                      width: fill === 'half' ? '50%' : '100%'
                    }}
                  >
                    <StarFilledIcon color={fillColor} color2="transparent" />
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>
      <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
        {`(${formatValue ? formatValue(rating) : (rating ?? '-')}/${max} étoiles)`}
      </Typography>
    </div>
  )
}

export default Rating
